import { useEffect, useState } from 'react';
import { debounce } from 'lodash';

interface StaticMeasures {
    iconWidthPixels: number;
    spacingUnitPixels: number;
    spaceBetweenIconAndLabel: number;
    containerSpacingAtStart: number;
    containerSpacingAtEnd: number;
    extrasWidth: number;
}

interface Params {
    shouldFitParent: boolean;
    label?: string;
    values: string[];
    containerWidth: number;
    labelWidth: number;
    valuesElements: (HTMLSpanElement | null)[];
    staticMeasures: StaticMeasures;
    onResizing?: () => void;
    onFinishSizing?: () => void;
}

const concatValues = (values: string[]) => values[0]?.concat(...values.slice(1));

export const useGetMaxValuesToDisplay = ({
    shouldFitParent,
    label,
    values,
    containerWidth,
    labelWidth,
    valuesElements,
    staticMeasures,
    onResizing,
    onFinishSizing
}: Params) => {
    const numberOfValues = values.length;
    const [sizing, setSizing] = useState(false);
    const [maxValuesToDisplay, setMaxValuesToDisplay] = useState(numberOfValues);
    const valuesDep = concatValues(values);
    const {
        iconWidthPixels,
        spacingUnitPixels,
        spaceBetweenIconAndLabel,
        containerSpacingAtStart,
        containerSpacingAtEnd,
        extrasWidth
    } = staticMeasures;

    const staticSpacingsPixels =
        spacingUnitPixels * (spaceBetweenIconAndLabel + containerSpacingAtStart + containerSpacingAtEnd);
    const staticWidthPixels = staticSpacingsPixels + iconWidthPixels;

    useEffect(() => {
        if (shouldFitParent) {
            setSizing(true);
        }
    }, [shouldFitParent]);

    useEffect(() => {
        if (sizing) {
            onResizing?.();
        } else {
            onFinishSizing?.();
        }
    }, [sizing, onResizing, onFinishSizing]);

    useEffect(() => {
        const allValuesElementsAreReady = valuesElements.reduce((allReady, element) => allReady && !!element, true);
        const readyToFindSize = !(!containerWidth || (!!label && !labelWidth) || !allValuesElementsAreReady);
        const shouldResize = sizing && readyToFindSize;

        const getMaxItemsToDisplay = (): number => {
            for (
                let numberOfValuesToDisplay = 0, totalWidth = staticWidthPixels + labelWidth;
                numberOfValuesToDisplay < numberOfValues;
                numberOfValuesToDisplay++
            ) {
                const elementWidth = valuesElements[numberOfValuesToDisplay]?.offsetWidth ?? 0;
                totalWidth += elementWidth;

                if (totalWidth > containerWidth) {
                    const totalWidthWidthWithExtraLabel = totalWidth - elementWidth + extrasWidth;
                    if (totalWidthWidthWithExtraLabel > containerWidth) {
                        return Math.max(numberOfValuesToDisplay - 1, 0);
                    }
                    return numberOfValuesToDisplay;
                }
            }

            return numberOfValues;
        };

        if (shouldResize) {
            setMaxValuesToDisplay(getMaxItemsToDisplay());
            setSizing(false);
        }
    }, [
        sizing,
        label,
        valuesDep,
        containerWidth,
        labelWidth,
        numberOfValues,
        valuesElements,
        staticWidthPixels,
        extrasWidth
    ]);

    useEffect(() => {
        const handleResize = debounce(() => {
            setSizing(true);
        }, 100);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return { maxValuesToDisplay };
};
